import { useCallback, useState } from 'react'
import includes from 'lodash/includes'
import pickBy from 'lodash/pickBy'
import { DelimitedArrayParam, NumberParam, StringParam, ArrayParam } from 'use-query-params'
import {
  BookableShipmentSavedSearch,
  BookableShipmentSearchQueryParams,
} from 'apps/fleet-planner/types'
import {
  BookableShipmentSavedSearchFiltersKeys,
  TBookableShipmentSavedSearchFilters,
} from 'apps/fleet-planner/types/bookableShipmentSavedSearch'
import { useQueryParams } from 'utils/hooks'

export const useLaneFilters = () => {
  const [selectedFilter, setSelectedFilter] = useState<
    keyof BookableShipmentSavedSearch | undefined
  >()

  const [params, setParams] = useState<BookableShipmentSearchQueryParams>({
    sortField: 'pickups',
    sortOrder: 'asc',
    pageNumber: 0,
  })

  const [queryParams, setQueryParams] = useQueryParams<TBookableShipmentSavedSearchFilters>({
    pickupCity: StringParam,
    pickupStateCode: StringParam,
    pickupStartDate: StringParam,
    pickupEndDate: StringParam,
    deliveryCity: StringParam,
    deliveryStateCode: StringParam,
    deliveryStartDate: StringParam,
    deliveryEndDate: StringParam,
    pickupMiles: NumberParam,
    deliveryMiles: NumberParam,
    trailerType: StringParam,
    loadedMilesMin: NumberParam,
    loadedMilesMax: NumberParam,
    pickupIsoDaysOfWeek: DelimitedArrayParam,
    pickupTimes: ArrayParam,
    id: NumberParam,
  })

  const [hasStartedSearch, setHasStartedSearch] = useState<boolean>(false)

  const changeParams = useCallback(
    (change: BookableShipmentSearchQueryParams, excludeParamsUpdate?: boolean) => {
      if (change.filters) {
        let filteredFilters: BookableShipmentSavedSearch = {}
        BookableShipmentSavedSearchFiltersKeys.forEach(key => {
          filteredFilters[key] = undefined
        })
        filteredFilters = {
          ...filteredFilters,
          ...pickBy(
            change.filters || {},
            (value, key) => value !== null && includes(BookableShipmentSavedSearchFiltersKeys, key)
          ),
        }
        change.filters = {
          ...filteredFilters,
          // DelimitedArrayParam serializes arrays as comma-separated strings, we are expecting them as numbers
          pickupIsoDaysOfWeek: filteredFilters.pickupIsoDaysOfWeek?.map(s => Number(s)),
          // if dropTrailer is deselcted just remove it from the query params
          dropTrailer: filteredFilters.dropTrailer ? true : undefined,
        }
      }
      const { filters = {} } = change
      // Clear the queryParam id when changing filters, so we don't trigger another savedSearch lookup.
      if (!excludeParamsUpdate) {
        setQueryParams({ id: undefined, ...filters })
      }
      setParams(prev => ({
        ...prev,
        ...change,
      }))
    },
    []
  )

  const toggleSelectedFilter = useCallback((keyName: keyof BookableShipmentSavedSearch) => {
    setSelectedFilter(prev => {
      // If it's already active, remove it
      if (prev === keyName) {
        return undefined
      }
      return keyName
    })
  }, [])

  const createToggleSelectedFilterHandler = useCallback(
    (keyName: keyof BookableShipmentSavedSearch) => () => {
      toggleSelectedFilter(keyName)
    },
    [toggleSelectedFilter]
  )

  const clearAllFilters = () => {
    changeParams({
      pageNumber: 0,
      sortField: 'pickups',
      sortOrder: 'asc',
      // TODO: We shouldn't have to manually set each filter to undefined in order to clear the queryParams.
      filters: {
        pickupCity: undefined,
        pickupStateCode: undefined,
        pickupMiles: undefined,
        pickupStartDate: undefined,
        pickupEndDate: undefined,
        pickupIsoDaysOfWeek: undefined,
        deliveryCity: undefined,
        deliveryStateCode: undefined,
        deliveryMiles: undefined,
        deliveryStartDate: undefined,
        deliveryEndDate: undefined,
        trailerType: undefined,
        loadedMilesMin: undefined,
        loadedMilesMax: undefined,
        dropTrailer: undefined,
        pickupTimes: undefined,
      },
    })
    setHasStartedSearch(false)
  }

  return {
    params,
    setParams,
    changeParams,
    selectedFilter,
    setSelectedFilter,
    toggleSelectedFilter,
    createToggleSelectedFilterHandler,
    hasStartedSearch,
    setHasStartedSearch,
    queryParams,
    setQueryParams,
    clearAllFilters,
  }
}
